import React, { useState, useEffect, useMemo, useRef } from "react";
import DashboardFooter from "../components/DashboardFooter";
import { NotificationManager } from "react-notifications";
import { handleWheel, handleMouseEnter, handleMouseLeave } from '../utils/Scroll';
// Full endpoint (list + create + update all live on this one URL)
const API_URL = `${process.env.REACT_APP_HOST_API_URL}/admin-user/get-admin-products/`;

// Only two real layout styles exist on the backend
const LAYOUT_STYLE_OPTIONS = ["high_light", "regular"];

// ---------- default JSON shown when creating a new package ----------
const buildDefaultDescriptionTemplate = () =>
  JSON.stringify(
    {
      package_name: "",
      package_type: "",
      description: [""],
      packages: [{ id: Date.now(), duration_hours: 1, price_sgd: 0, type: "single_session" }],
      policies: {
        booking_rules: [""],
      },
    },
    null,
    2
  );

const EXAMPLE_JSON = JSON.stringify(
  {
    package_name: "HOURLY GENERAL CLEANING",
    package_type: "Cleaning equipment and cleaning solutions will be provided by the company.",
    description: [
      "Our Hourly General House Cleaning service is designed for customers who need flexible cleaning based on selected duration. This service includes routine home cleaning, dusting, sweeping, mopping, wiping surfaces, bathroom and kitchen cleaning, and general tidying up.",
    ],
    packages: [{ id: 1, duration_hours: 2, price_sgd: 30, type: "single_session" }],
    policies: { booking_rules: ["Booking to be made at least 24 hours in advance."] },
  },
  null,
  2
);

const emptyForm = () => ({
  layout_style: "regular",
  status: true,
  is_premium: false,
  rawJson: buildDefaultDescriptionTemplate(),
  iconFile: null,
  iconPreview: null,
  viewImagesFile: null,
  viewImagesPreview: null,
  existingMultipleImages: [], // [{id, image_url}]
  newMultipleImageFiles: [], // File[]
  deleteImageIds: [],
});

// Parse a raw description JSON string just for read-only display on the cards.
// Never used to drive editing anymore — editing is always raw JSON.
const parseDescriptionJson = (raw) => {
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return {
      ok: true,
      package_name: parsed.package_name || "",
      package_type: parsed.package_type || "",
      description_points: Array.isArray(parsed.description) ? parsed.description.filter(Boolean) : [],
      packages: Array.isArray(parsed.packages) ? parsed.packages : [],
    };
  } catch (e) {
    return { ok: false };
  }
};

// ---------- JSON syntax highlighting (lightweight, no dependency) ----------
const highlightJson = (rawValue) => {
  if (!rawValue) return "";
  const escaped = rawValue.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = "jv-number";
      if (/^"/.test(match)) {
        cls = /:$/.test(match) ? "jv-key" : "jv-string";
      } else if (/^(true|false)$/.test(match)) {
        cls = "jv-bool";
      } else if (/^null$/.test(match)) {
        cls = "jv-null";
      }
      return `<span class="${cls}">${match}</span>`;
    }
  );
};

// ---------- JSON validation with a human-readable location ----------
const getJsonError = (str) => {
  if (!str || !str.trim()) return "Description JSON can't be empty";
  try {
    JSON.parse(str);
    return null;
  } catch (e) {
    const match = /position (\d+)/.exec(e.message);
    if (match) {
      const pos = parseInt(match[1], 10);
      const upToError = str.slice(0, pos);
      const lines = upToError.split("\n");
      const line = lines.length;
      const column = lines[lines.length - 1].length + 1;
      return `${e.message} — line ${line}, column ${column}`;
    }
    return e.message;
  }
};

// ---------- small inline icons (no extra dependency) ----------
const Icon = {
  Box: (props) => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 8L12 3 3 8v8l9 5 9-5V8z" />
      <path d="M3 8l9 5 9-5M12 13v8" />
    </svg>
  ),
  Shield: (props) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  Crown: (props) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M2 8l4 4 6-8 6 8 4-4-2 11H4L2 8z" />
    </svg>
  ),
  List: (props) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  ),
  Search: (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  ),
  Upload: (props) => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M4 16v3a2 2 0 002 2h12a2 2 0 002-2v-3" />
    </svg>
  ),
  Pencil: (props) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  ),
  Dots: (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  ),
  X: (props) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
};

const ManagePackages = () => {
  const token = localStorage.getItem("token");

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create mode
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [premiumFilter, setPremiumFilter] = useState("all");
  const [layoutFilter, setLayoutFilter] = useState("all");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [iconDragOver, setIconDragOver] = useState(false);
  const [galleryDragOver, setGalleryDragOver] = useState(false);

  const overlayRef = useRef(null);
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const iconInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const authHeaders = token ? { Authorization: "Bearer " + token } : {};

  // ---------- Fetch packages ----------
  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL, {
        method: "GET",
        headers: { ...authHeaders },
      });
      if (!res.ok) throw new Error("Failed to fetch packages");
      const data = await res.json();
      setPackages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      NotificationManager.error("Error fetching packages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
  if (showModal) {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }
}, [showModal]);

  // ---------- Derived: parsed display info per package ----------
  const parsedPackages = useMemo(
    () =>
      packages.map((p) => {
        const parsed = parseDescriptionJson(p.description);
        const prices = parsed.ok ? parsed.packages.map((x) => Number(x.price_sgd) || 0) : [];
        return {
          ...p,
          _name: parsed.ok && parsed.package_name ? parsed.package_name : "Untitled package",
          _type: parsed.ok ? parsed.package_type : "",
          _descSnippet: parsed.ok && parsed.description_points.length ? parsed.description_points.join(" ") : "",
          _priceRange: prices.length
            ? Math.min(...prices) === Math.max(...prices)
              ? `SGD ${Math.min(...prices)}`
              : `SGD ${Math.min(...prices)} – ${Math.max(...prices)}`
            : "",
        };
      }),
    [packages]
  );

  const filteredPackages = useMemo(() => {
    return parsedPackages.filter((p) => {
      const matchesSearch =
        !search ||
        p._name.toLowerCase().includes(search.toLowerCase()) ||
        p._type.toLowerCase().includes(search.toLowerCase()) ||
        (p.layout_style || "").toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || (statusFilter === "active" && p.status) || (statusFilter === "inactive" && !p.status);
      const matchesPremium =
        premiumFilter === "all" || (premiumFilter === "premium" && p.is_premium) || (premiumFilter === "standard" && !p.is_premium);
      const matchesLayout = layoutFilter === "all" || p.layout_style === layoutFilter;
      return matchesSearch && matchesStatus && matchesPremium && matchesLayout;
    });
  }, [parsedPackages, search, statusFilter, premiumFilter, layoutFilter]);

  // ---------- Stats ----------
  const stats = useMemo(() => {
    const total = packages.length;
    const active = packages.filter((p) => p.status).length;
    const premium = packages.filter((p) => p.is_premium).length;
    const standard = total - premium;
    return { total, active, premium, standard };
  }, [packages]);

  // ---------- Modal open/close ----------
  const openAddForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEditForm = (pkg) => {
    let rawJson;
    try {
      rawJson = JSON.stringify(JSON.parse(pkg.description), null, 2);
    } catch {
      // Keep whatever is stored, even if broken, so it can be seen and fixed directly
      rawJson = pkg.description || "";
    }

    setEditingId(pkg.id);
    setForm({
      ...emptyForm(),
      layout_style: LAYOUT_STYLE_OPTIONS.includes(pkg.layout_style) ? pkg.layout_style : "regular",
      status: !!pkg.status,
      is_premium: !!pkg.is_premium,
      rawJson,
      iconPreview: pkg.icon_url || null,
      viewImagesPreview: pkg.view_images_url || null,
      existingMultipleImages: pkg.multiple_images || [],
    });
    setOpenMenuId(null);
    setShowModal(true);
  };

  const closeForm = () => {
    setShowModal(false);
    setEditingId(null);
  };

  // ---------- Field helpers ----------
  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const jsonError = useMemo(() => getJsonError(form.rawJson), [form.rawJson]);
  const lineCount = useMemo(() => (form.rawJson ? form.rawJson.split("\n").length : 1), [form.rawJson]);

  const handleFormatJson = () => {
    try {
      const pretty = JSON.stringify(JSON.parse(form.rawJson), null, 2);
      updateField("rawJson", pretty);
    } catch {
      NotificationManager.error("Can't format — fix the highlighted JSON error first");
    }
  };

  const handleUseExample = () => {
    updateField("rawJson", EXAMPLE_JSON);
    NotificationManager.info("Example JSON loaded — edit it to match your package");
  };

  const handleEditorScroll = () => {
    if (overlayRef.current && textareaRef.current) {
      overlayRef.current.scrollTop = textareaRef.current.scrollTop;
      overlayRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
    if (lineNumbersRef.current && textareaRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  // ---------- File handlers ----------
  const handleIconChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, iconFile: file, iconPreview: URL.createObjectURL(file) }));
  };

  const handleViewImagesChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, viewImagesFile: file, viewImagesPreview: URL.createObjectURL(file) }));
  };

  // Multiple images ARE supported here: `multiple` on the input plus this handler
  // appends every selected file onto newMultipleImageFiles, and each one is sent
  // as its own "multiple_images" field in the FormData on submit.
  const handleMultipleImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setForm((f) => ({ ...f, newMultipleImageFiles: [...f.newMultipleImageFiles, ...files] }));
  };

  const removeNewMultipleImage = (idx) =>
    setForm((f) => ({
      ...f,
      newMultipleImageFiles: f.newMultipleImageFiles.filter((_, i) => i !== idx),
    }));

  const removeIconFile = () => setForm((f) => ({ ...f, iconFile: null, iconPreview: null }));

  const toggleExistingImageForDeletion = (imageId) =>
    setForm((f) => {
      const isMarked = f.deleteImageIds.includes(imageId);
      return {
        ...f,
        deleteImageIds: isMarked ? f.deleteImageIds.filter((id) => id !== imageId) : [...f.deleteImageIds, imageId],
      };
    });

  // Drag & drop wiring for the icon dropzone
  const handleIconDrop = (e) => {
    e.preventDefault();
    setIconDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, iconFile: file, iconPreview: URL.createObjectURL(file) }));
  };

  // Drag & drop wiring for the gallery dropzone
  const handleGalleryDrop = (e) => {
    e.preventDefault();
    setGalleryDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    setForm((f) => ({ ...f, newMultipleImageFiles: [...f.newMultipleImageFiles, ...files] }));
  };

  // ---------- Submit (create or update) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = getJsonError(form.rawJson);
    if (err) {
      NotificationManager.error("Fix the description JSON before saving: " + err);
      return;
    }

    setSaving(true);

    const fd = new FormData();
    fd.append("layout_style", form.layout_style);
    fd.append("description", form.rawJson);
    fd.append("status", form.status);
    fd.append("is_premium", form.is_premium);
    if (form.iconFile) fd.append("icon", form.iconFile);

    if (editingId) {
      fd.append("id", editingId);
      if (form.viewImagesFile) fd.append("view_images", form.viewImagesFile);
      form.newMultipleImageFiles.forEach((file) => fd.append("multiple_images", file));
      form.deleteImageIds.forEach((id) => fd.append("delete_image_ids", id));
    } else {
      form.newMultipleImageFiles.forEach((file) => fd.append("multiple_images", file));
    }

    try {
      const res = await fetch(API_URL, {
        method: editingId ? "PATCH" : "POST",
        headers: { ...authHeaders }, // do NOT set Content-Type, browser sets multipart boundary
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");

      NotificationManager.success(editingId ? "Package updated successfully" : "Package created successfully");
      closeForm();
      fetchPackages();
    } catch (err) {
      console.error(err);
      NotificationManager.error(err.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 pb-2 px-3" onClick={() => openMenuId && setOpenMenuId(null)}>
      <style>{`
        .mp-header {
          position: relative;
          overflow: hidden;
          background: linear-gradient(120deg, #0a5fc4 0%, #1e88e8 55%, #4fb3f0 100%);
          border-radius: 18px;
        }
        .mp-header::before, .mp-header::after {
          content: "";
          position: absolute;
          border-radius: 50%;
          background: rgba(255,255,255,.10);
        }
        .mp-header::before { width: 220px; height: 220px; top: -110px; right: 60px; }
        .mp-header::after { width: 90px; height: 90px; top: 20px; right: 230px; background: rgba(255,255,255,.14); }
        .mp-header-inner { position: relative; z-index: 1; }
        .mp-header-icon {
          width: 52px; height: 52px; border-radius: 14px;
          background: rgba(255,255,255,.18);
          display: flex; align-items: center; justify-content: center;
          color: #fff;
        }
        .mp-stat-card {
          background: rgba(255,255,255,.14);
          border: 1px solid rgba(255,255,255,.18);
          border-radius: 14px;
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          height: 100%;
        }
        .mp-stat-icon {
          width: 40px; height: 40px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          background: #fff;
        }
        .mp-stat-icon.blue { color: #0a5fc4; }
        .mp-stat-icon.green { background: #eafaf0; color: #16a34a; }
        .mp-stat-icon.amber { background: #ffb443; color: #7a4a00; }
        .mp-stat-icon.indigo { background: #fff; color: #2a5cd8; }
        .mp-stat-value { font-size: 22px; font-weight: 700; color: #fff; line-height: 1.1; }
        .mp-stat-label { font-size: 12.5px; color: rgba(255,255,255,.85); font-weight: 500; }
        .mp-stat-sub { font-size: 11px; color: rgba(255,255,255,.65); }

        .mp-filter-bar {
          background: #fff;
          border: 1px solid #eef1f5;
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(16,24,40,.05);
          padding: 14px 16px;
        }
        .mp-search-wrap { position: relative; }
        .mp-search-wrap .mp-search-icon {
          position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #98a2b3;
        }
        .mp-search-wrap input {
          padding-left: 38px; border-radius: 10px; border: 1px solid #e4e7ec;
        }
        .mp-filter-select {
          border-radius: 10px; border: 1px solid #e4e7ec; font-size: 14px;
        }
        .mp-add-btn {
          border-radius: 10px; font-weight: 600; background: #0a5fc4; border: none;
          white-space: nowrap;
        }
        .mp-add-btn:hover { background: #084c9e; }

        .mp-card {
          background: #fff;
          border: 1px solid #edf0f4;
          border-left: 4px solid #0a5fc4;
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(16,24,40,.05);
          transition: box-shadow .15s ease, transform .15s ease;
          height: 100%;
        }
        .mp-card:hover { box-shadow: 0 10px 24px rgba(16,24,40,.10); transform: translateY(-2px); }
        .mp-card-img {
          width: 100%;
          height: 130px;
          border-radius: 10px;
          overflow: hidden;
          background: #f3f5f8;
          display: flex; align-items: center; justify-content: center;
        }
        .mp-card-img img { width: 100%; height: 100%; object-fit: cover; }
        .mp-card-noimg { font-size: 11px; color: #98a2b3; }
        .mp-card-title { font-size: 15px; font-weight: 700; color: #101828; }
        .mp-card-desc {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .mp-badge {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; font-weight: 600;
          padding: 3px 10px; border-radius: 999px; white-space: nowrap;
        }
        .mp-badge-highlight { background: #eef2ff; color: #4338ca; }
        .mp-badge-regular { background: #eef4ff; color: #2563eb; }
        .mp-badge-standard { background: #eef4ff; color: #2563eb; }
        .mp-badge-premium { background: #fff4e0; color: #b7791f; }
        .mp-badge-success { color: #16a34a; }
        .mp-badge-danger { color: #dc2626; }
        .mp-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
        .mp-dot.success { background: #16a34a; }
        .mp-dot.danger { background: #dc2626; }

        .mp-price { color: #0a5fc4; font-weight: 700; font-size: 15px; }

        .mp-btn-edit {
          border-radius: 8px;
          border: 1px solid #0a5fc4;
          color: #0a5fc4;
          font-weight: 600;
          background: #fff;
          font-size: 13px;
        }
        .mp-btn-edit:hover { background: #0a5fc4; color: #fff; }

        .mp-kebab {
          width: 30px; height: 30px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid #e4e7ec; background: #fff; color: #667085;
          position: relative;
        }
        .mp-kebab-menu {
          position: absolute; right: 0; top: 36px; z-index: 10;
          background: #fff; border: 1px solid #eef1f5; border-radius: 10px;
          box-shadow: 0 8px 20px rgba(16,24,40,.12);
          min-width: 130px; padding: 6px;
        }
        .mp-kebab-menu button {
          width: 100%; text-align: left; border: none; background: none;
          padding: 8px 10px; border-radius: 6px; font-size: 13px; color: #344054;
        }
        .mp-kebab-menu button:hover { background: #f2f4f7; }

        .mp-modal-overlay { background: rgba(16,24,40,.55); backdrop-filter: blur(2px); }
        .mp-modal { border-radius: 16px; overflow: hidden; }
        .mp-modal-header {
          background: linear-gradient(120deg, #0a5fc4 0%, #1e88e8 100%);
          color: #fff;
        }
        .mp-modal-close {
          width: 30px; height: 30px; border-radius: 50%;
          border: 1px solid rgba(255,255,255,.5);
          background: transparent; color: #fff;
          display: flex; align-items: center; justify-content: center;
        }
        .mp-modal-close:hover { background: rgba(255,255,255,.15); }

        .mp-dropzone {
          border: 1.5px dashed #cdd5e0;
          border-radius: 12px;
          padding: 22px 12px;
          text-align: center;
          color: #667085;
          cursor: pointer;
          transition: border-color .15s ease, background .15s ease;
          background: #fbfcfe;
        }
        .mp-dropzone:hover, .mp-dropzone.drag-over { border-color: #0a5fc4; background: #f0f7ff; }
        .mp-dropzone small { display: block; color: #98a2b3; margin-top: 2px; }

        .mp-thumb { position: relative; }
        .mp-thumb img {
          width: 64px; height: 64px; object-fit: cover; border-radius: 8px; border: 1px solid #e4e7ec;
        }
        .mp-thumb .mp-thumb-x {
          position: absolute; top: -6px; right: -6px;
          width: 20px; height: 20px; border-radius: 50%;
          background: #e11d48; color: #fff; border: 2px solid #fff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
        }
        .mp-thumb.marked img { opacity: .35; }
        .mp-thumb .mp-thumb-x.undo { background: #667085; }

        .mp-section-label { font-size: 13.5px; font-weight: 600; color: #344054; margin-bottom: 6px; }

        .mp-json-help { font-size: 13px; color: #0a5fc4; font-weight: 600; text-decoration: none; }
        .mp-json-help:hover { text-decoration: underline; }

        .mp-json-editor-wrap {
          position: relative;
          display: flex;
          border: 1px solid #23303f;
          border-radius: 10px;
          overflow: hidden;
          background: #0d1117;
          height: 650px;
          overscroll-behavior: contain;
        }
        .mp-json-linenumbers {
          flex-shrink: 0;
          width: 42px;
          padding: 14px 8px 14px 0;
          text-align: right;
          font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
          font-size: 12.5px;
          line-height: 1.6;
          color: #4b5563;
          background: #0b0f14;
          overflow: hidden;
          user-select: none;
        }
        .mp-json-editor-body { position: relative; flex: 1 1 auto; }
        .mp-json-highlight, .mp-json-textarea {
          margin: 0;
          padding: 14px 16px;
          font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
          font-size: 12.5px;
          line-height: 1.6;
          white-space: pre;
          overflow: auto;
          overscroll-behavior: contain;
        }
        .mp-json-highlight {
          position: absolute;
          inset: 0;
          color: #c9d1d9;
          pointer-events: none;
        }
        .mp-json-textarea {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          resize: none;
          border: none;
          outline: none;
          background: transparent;
          color: transparent;
          caret-color: #f0f6fc;
        }
        .jv-key { color: #79c0ff; }
        .jv-string { color: #a5d6ff; }
        .jv-number { color: #f0883e; }
        .jv-bool { color: #d2a8ff; }
        .jv-null { color: #ff7b72; }

        .mp-json-error {
          background: #fef3f2; color: #b42318; border: 1px solid #fda29b;
          border-radius: 8px; padding: 8px 12px; font-size: 12.5px;
        }
        .mp-json-ok {
          background: #ecfdf3; color: #067647; border: 1px solid #abefc6;
          border-radius: 8px; padding: 8px 12px; font-size: 12.5px;
        }
      `}</style>

      <div className="flex-grow-1">
        <div className="container-fluid pb-2 px-2 px-md-4" style={{ paddingTop: "20px" }}>
          {/* Header */}
          <div className="mp-header mb-4 p-4">
            <div className="mp-header-inner">
              <div className="d-flex align-items-center gap-3 mb-4">
                <div className="mp-header-icon">
                  <Icon.Box />
                </div>
                <div>
                  <h3 className="text-white mb-1 fw-bold">Manage Packages</h3>
                  <p className="text-white mb-0 opacity-75">Create, edit and manage all your service packages</p>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-6 col-md-3">
                  <div className="mp-stat-card">
                    <div className="mp-stat-icon blue">
                      <Icon.Box />
                    </div>
                    <div>
                      <div className="mp-stat-label">Total Packages</div>
                      <div className="mp-stat-value">{stats.total}</div>
                      <div className="mp-stat-sub">All packages</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="mp-stat-card">
                    <div className="mp-stat-icon green">
                      <Icon.Shield />
                    </div>
                    <div>
                      <div className="mp-stat-label">Active Packages</div>
                      <div className="mp-stat-value">{stats.active}</div>
                      <div className="mp-stat-sub">Currently active</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="mp-stat-card">
                    <div className="mp-stat-icon amber">
                      <Icon.Crown />
                    </div>
                    <div>
                      <div className="mp-stat-label">Premium Packages</div>
                      <div className="mp-stat-value">{stats.premium}</div>
                      <div className="mp-stat-sub">Premium services</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="mp-stat-card">
                    <div className="mp-stat-icon indigo">
                      <Icon.List />
                    </div>
                    <div>
                      <div className="mp-stat-label">Standard Packages</div>
                      <div className="mp-stat-value">{stats.standard}</div>
                      <div className="mp-stat-sub">Standard services</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mp-filter-bar row g-2 align-items-center mb-4">
            <div className="col-12 col-md-5">
              <div className="mp-search-wrap">
                <span className="mp-search-icon">
                  <Icon.Search />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, type or layout..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="col-6 col-md-2">
              <select className="form-select mp-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="col-6 col-md-2">
              <select className="form-select mp-filter-select" value={premiumFilter} onChange={(e) => setPremiumFilter(e.target.value)}>
                <option value="all">All Premium</option>
                <option value="premium">Premium</option>
                <option value="standard">Standard</option>
              </select>
            </div>
            <div className="col-6 col-md-2">
              <select className="form-select mp-filter-select" value={layoutFilter} onChange={(e) => setLayoutFilter(e.target.value)}>
                <option value="all">All Layouts</option>
                {LAYOUT_STYLE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-1">
              <button className="btn btn-primary mp-add-btn w-100 py-2" onClick={openAddForm}>
                + Add
              </button>
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="text-center py-5 text-muted">Loading packages...</div>
          ) : filteredPackages.length === 0 ? (
            <div className="text-center py-5 text-muted">No packages found.</div>
          ) : (
            <div className="row g-3">
              {filteredPackages.map((pkg) => (
                <div className="col-12 col-md-6 col-lg-4" key={pkg.id}>
                  <div className="mp-card p-3 d-flex flex-column">
                    <div className="d-flex gap-3 mb-2">
                      <div className="mp-card-img flex-shrink-0" style={{ width: 90, height: 90 }}>
                        {pkg.view_images_url ? (
                          <img src={pkg.view_images_url} alt={pkg._name} />
                        ) : (
                          <span className="mp-card-noimg">No image</span>
                        )}
                      </div>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="mp-card-title text-uppercase mb-1">{pkg._name}</div>
                        <div className="d-flex flex-wrap gap-1 mb-1">
                          <span className={`mp-badge ${pkg.layout_style === "high_light" ? "mp-badge-highlight" : "mp-badge-regular"}`}>
                            {pkg.layout_style}
                          </span>
                          {pkg.is_premium ? (
                            <span className="mp-badge mp-badge-premium">★ Premium</span>
                          ) : (
                            <span className="mp-badge mp-badge-standard">Standard</span>
                          )}
                        </div>
                        {pkg._priceRange && <div className="mp-price">{pkg._priceRange}</div>}
                      </div>
                    </div>

                    <p className="text-muted small mb-2 mp-card-desc" style={{ minHeight: 36 }}>
                      {pkg._descSnippet || pkg._type || "No description provided."}
                    </p>

                    <hr className="my-2" />

                    <div className="d-flex align-items-center justify-content-between">
                      <span className="d-inline-flex align-items-center gap-2 small fw-semibold">
                        <span className={`mp-dot ${pkg.status ? "success" : "danger"}`}></span>
                        <span className={pkg.status ? "mp-badge-success" : "mp-badge-danger"}>{pkg.status ? "Active" : "Inactive"}</span>
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        <button className="btn btn-sm mp-btn-edit px-3 d-inline-flex align-items-center gap-1" onClick={() => openEditForm(pkg)}>
                          <Icon.Pencil /> Edit
                        </button>
                        <div className="mp-kebab" onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === pkg.id ? null : pkg.id); }}>
                          <Icon.Dots />
                          {openMenuId === pkg.id && (
                            <div className="mp-kebab-menu" onClick={(e) => e.stopPropagation()}>
                              <button type="button" onClick={() => openEditForm(pkg)}>
                                Edit package
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---------------- Modal ---------------- */}
      {showModal && (
        <div
          className="mp-modal-overlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 2000 }}
          onClick={(e) => e.target === e.currentTarget && closeForm()}
          onWheel={handleWheel}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="mp-modal bg-white shadow-lg" style={{ width: "94%", maxWidth: "1100px", maxHeight: "92vh", overflowY: "auto", overscrollBehavior: "contain", }}
            onWheel={handleWheel}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave} >
            <form onSubmit={handleSubmit}>
              <div className="mp-modal-header d-flex align-items-center justify-content-between px-4 py-3">
                <h5 className="mb-0 fw-bold">{editingId ? "Edit Package" : "Add / Edit Package"}</h5>
                <button type="button" className="mp-modal-close" onClick={closeForm}>
                  <Icon.X />
                </button>
              </div>

              <div className="px-4 py-4">
                <div className="row g-4">
                  {/* ---------- Left column ---------- */}
                  <div className="col-12 col-lg-5">
                    <div className="row g-3 mb-3">
                      <div className="col-4">
                        <label className="form-label mp-section-label">Layout Style *</label>
                        <select
                          className="form-select mp-filter-select"
                          value={form.layout_style}
                          onChange={(e) => updateField("layout_style", e.target.value)}
                        >
                          {LAYOUT_STYLE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-4">
                        <label className="form-label mp-section-label">Status *</label>
                        <select
                          className="form-select mp-filter-select"
                          value={form.status ? "active" : "inactive"}
                          onChange={(e) => updateField("status", e.target.value === "active")}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div className="col-4">
                        <label className="form-label mp-section-label">Is Premium *</label>
                        <select
                          className="form-select mp-filter-select"
                          value={form.is_premium ? "yes" : "no"}
                          onChange={(e) => updateField("is_premium", e.target.value === "yes")}
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                    </div>

                    {/* Icon image */}
                    <div className="mb-3">
                      <label className="form-label mp-section-label">Icon Image</label>
                      <div className="d-flex gap-2 align-items-start">
                        <div
                          className={`mp-dropzone flex-grow-1 ${iconDragOver ? "drag-over" : ""}`}
                          onClick={() => iconInputRef.current?.click()}
                          onDragOver={(e) => { e.preventDefault(); setIconDragOver(true); }}
                          onDragLeave={() => setIconDragOver(false)}
                          onDrop={handleIconDrop}
                        >
                          <Icon.Upload />
                          <div className="mt-1 small fw-semibold">Drag &amp; drop or click to upload</div>
                          <small>PNG, JPG, JPEG (Max 2MB)</small>
                          <input ref={iconInputRef} type="file" accept="image/*" className="d-none" onChange={handleIconChange} />
                        </div>
                        {form.iconPreview && (
                          <div className="mp-thumb flex-shrink-0">
                            <img src={form.iconPreview} alt="icon preview" />
                            <span className="mp-thumb-x" onClick={removeIconFile}>
                              <Icon.X />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Gallery / view images */}
                    <div className="mb-3">
                      <label className="form-label mp-section-label">View Images (Multiple)</label>
                      <div
                        className={`mp-dropzone ${galleryDragOver ? "drag-over" : ""}`}
                        onClick={() => galleryInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setGalleryDragOver(true); }}
                        onDragLeave={() => setGalleryDragOver(false)}
                        onDrop={handleGalleryDrop}
                      >
                        <Icon.Upload />
                        <div className="mt-1 small fw-semibold">Drag &amp; drop images here</div>
                        <small>or click to browse — PNG, JPG, JPEG (Max 5MB each)</small>
                        <input ref={galleryInputRef} type="file" accept="image/*" multiple className="d-none" onChange={handleMultipleImagesChange} />
                      </div>

                      {form.newMultipleImageFiles.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {form.newMultipleImageFiles.map((file, idx) => (
                            <div key={idx} className="mp-thumb">
                              <img src={URL.createObjectURL(file)} alt="" />
                              <span className="mp-thumb-x" onClick={() => removeNewMultipleImage(idx)}>
                                <Icon.X />
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Existing images */}
                    {editingId && form.existingMultipleImages.length > 0 && (
                      <div className="mb-3">
                        <label className="form-label mp-section-label">Existing Images</label>
                        <div className="d-flex flex-wrap gap-2">
                          {form.existingMultipleImages.map((img) => {
                            const marked = form.deleteImageIds.includes(img.id);
                            return (
                              <div key={img.id} className={`mp-thumb ${marked ? "marked" : ""}`}>
                                <img src={img.image_url} alt="" />
                                <span
                                  className={`mp-thumb-x ${marked ? "undo" : ""}`}
                                  onClick={() => toggleExistingImageForDeletion(img.id)}
                                  title={marked ? "Undo remove" : "Remove image"}
                                >
                                  {marked ? "↺" : <Icon.X />}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Cover image — only relevant once a package exists */}
                    {editingId && (
                      <div className="mb-1">
                        <label className="form-label mp-section-label">Cover Image</label>
                        <input type="file" accept="image/*" className="form-control mp-filter-select" onChange={handleViewImagesChange} />
                        {form.viewImagesPreview && (
                          <img
                            src={form.viewImagesPreview}
                            alt="cover preview"
                            className="mt-2 rounded"
                            style={{ width: '350px', height: '200px', objectFit: "cover" }}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {/* ---------- Right column: JSON editor ---------- */}
                  <div className="col-12 col-lg-7">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="form-label mp-section-label mb-0">Description (JSON) *</label>
                      <button type="button" className="btn btn-sm p-0 border-0 mp-json-help" onClick={handleUseExample}>
                        Need help? View Example
                      </button>
                    </div>

                    <div className="mp-json-editor-wrap"
                   onWheel={handleWheel}
  onMouseEnter={handleMouseEnter}
  onMouseLeave={handleMouseLeave} >
                      <div ref={lineNumbersRef} className="mp-json-linenumbers">
                        {Array.from({ length: lineCount }, (_, i) => (
                          <div key={i}>{i + 1}</div>
                        ))}
                      </div>
                      <div className="mp-json-editor-body">
                        <pre
                          ref={overlayRef}
                          className="mp-json-highlight"
                          aria-hidden="true"
                          dangerouslySetInnerHTML={{ __html: highlightJson(form.rawJson) }}
                        />
                        <textarea
                          ref={textareaRef}
                          className="mp-json-textarea"
                          spellCheck={false}
                          value={form.rawJson}
                          onChange={(e) => updateField("rawJson", e.target.value)}
                          onScroll={handleEditorScroll}
                        />
                      </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-2">
                      <div className="flex-grow-1 me-2">
                        {jsonError ? <div className="mp-json-error">⚠ {jsonError}</div> : <div className="mp-json-ok">✓ Valid JSON</div>}
                      </div>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleFormatJson}>
                        Format
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 border-top px-4 py-3">
                <button type="button" className="btn btn-outline-secondary" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary mp-add-btn px-4" disabled={saving || !!jsonError}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DashboardFooter />
    </div>
  );
};

export default ManagePackages;